import {filter} from 'lodash';

import {ParentRoutes, ROUTE_PATH} from 'constants/routes';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {userAuthUtils} from 'utils';

import {MenuItemInfo} from './types';

const PAGE_NAMES_T_KEYS = TRANSLATION_KEYS.USER.PAGE_NAMES;

export const getMenuItems = ({t, clientConnectionsEnabled, incomingSessionCount}): MenuItemInfo[] => {
    const userPermissions = userAuthUtils.getUserPermissions();
    const items: MenuItemInfo[] = [
        {
            icon: 'phone',
            activeIcon: 'phone-filled',
            badge: incomingSessionCount || null,
            headerTitle: t(TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TITLE),
            link: ROUTE_PATH.USER.ONLINE._SELF,
            hidden: !userAuthUtils.isParentRouteEnabled(ParentRoutes.Online, userPermissions),
            titleTKey: PAGE_NAMES_T_KEYS.ONLINE.SELF,
            subItems: [
                {
                    link: ROUTE_PATH.USER.ONLINE.SESSIONS,
                    hidden: !userPermissions?.online?.sessions?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ONLINE.SESSIONS,
                },
                {
                    link: ROUTE_PATH.USER.ONLINE.CONVERSATIONS,
                    hidden: !userPermissions?.online?.conversations?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ONLINE.CONVERSATIONS,
                },
            ]
        },
        {
            icon: 'rocket',
            activeIcon: 'rocket-filled',
            link: ROUTE_PATH.USER.ACTIVITY._SELF,
            hidden: !userAuthUtils.isParentRouteEnabled(ParentRoutes.Activity, userPermissions),
            titleTKey: PAGE_NAMES_T_KEYS.ACTIVITY.SELF,
            subItems: [
                {
                    link: ROUTE_PATH.USER.ACTIVITY.SESSION_HISTORY,
                    hidden: !userPermissions?.activity?.sessionHistory?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACTIVITY.SESSION_HISTORY,
                },
                {
                    link: ROUTE_PATH.USER.ACTIVITY.PRE_SESSION_FLOW,
                    hidden: !userPermissions?.activity?.preSessionFlow?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACTIVITY.PRE_SESSION_FLOW,
                },
                {
                    link: ROUTE_PATH.USER.ACTIVITY.DASHBOARD,
                    hidden: !userPermissions?.activity?.dashboard?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACTIVITY.DASHBOARD.SELF,
                },
                {
                    link: ROUTE_PATH.USER.ACTIVITY.AGENTS_STATISTIC,
                    hidden: !userPermissions?.activity?.agentStatistic?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACTIVITY.AGENT_STATISTIC,
                },
                {
                    link: ROUTE_PATH.USER.ACTIVITY.COMPLIANCE,
                    hidden: !userPermissions?.activity?.compliance?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACTIVITY.COMPLIANCE,
                },
            ]
        },
        {
            icon: 'protected',
            activeIcon: 'protected-filled',
            link: ROUTE_PATH.USER.ACCOUNT._SELF,
            hidden: !userAuthUtils.isParentRouteEnabled(ParentRoutes.Account, userPermissions),
            titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.SELF,
            subItems: [
                {
                    link: ROUTE_PATH.USER.ACCOUNT.MY_PROFILE,
                    hidden: !userPermissions?.account?.myProfile?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.MY_PROFILE,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.SETTINGS,
                    hidden: !userPermissions?.account?.settings?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.SETTINGS.SELF,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.INTEGRATIONS,
                    hidden: !userPermissions?.account?.integrations?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.INTEGRATIONS,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.EDIT_USER,
                    hidden: !userPermissions?.account?.users?.currentInfo?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.USER_PROFILE,
                    activeOnly: true
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.CREATE_USER,
                    hidden: !userPermissions?.account?.users?.currentInfo?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.CREATE_USER,
                    activeOnly: true
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.USERS,
                    hidden: !userPermissions?.account?.users?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.USERS.SELF,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.GROUPS,
                    hidden: !userPermissions?.account?.groups?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.GROUPS,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.OUTGOING_MESSAGES,
                    hidden: !userPermissions?.account?.outgoingMessages?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.OUTGOING_MESSAGES.SELF,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.WORKING_HOURS,
                    hidden: !userPermissions?.account?.workingHours?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.WORKING_HOURS.SELF,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.BILLING,
                    hidden: !userPermissions?.account?.billing?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.BILLING,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.CLIENTS_CONNECTS,
                    hidden: !userPermissions?.account?.clientConnections?.enabled || !clientConnectionsEnabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.CLIENT_CONNECTIONS,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.ROLES,
                    hidden: !userPermissions?.account?.roles?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.ROLES,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.LIVE_HELP,
                    hidden: !userPermissions?.account?.liveHelp?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.LIVE_HELP,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.SESSION,
                    hidden: !userPermissions?.account?.session?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.SESSION.SELF,
                },
                {
                    link: ROUTE_PATH.USER.ACCOUNT.APPOINTMENTS,
                    hidden: !userPermissions?.account?.appointments?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.ACCOUNT.APPOINTMENTS,
                },
            ]
        },
        {
            icon: 'settings',
            activeIcon: 'settings-filled',
            link: ROUTE_PATH.USER.CONTENT._SELF,
            hidden: !userAuthUtils.isParentRouteEnabled(ParentRoutes.Content, userPermissions),
            titleTKey: PAGE_NAMES_T_KEYS.CONTENT.SELF,
            subItems: [
                {
                    link: ROUTE_PATH.USER.CONTENT.SESSION_RESULT,
                    hidden: !userPermissions?.content?.sessionResult?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.CONTENT.SESSION_RESULT.SELF,
                },
                {
                    link: ROUTE_PATH.USER.CONTENT.CLIENT_SESSION,
                    hidden: !userPermissions?.content?.clientSession?.enabled,
                    titleTKey: PAGE_NAMES_T_KEYS.CONTENT.CLIENT_SESSION.SELF,
                }
            ]
        }
    ];

    const mapWithTitle = (item: MenuItemInfo) => ({...item, title: t(item.titleTKey)});
    const filterHidden = (item: MenuItemInfo) => !item.hidden;

    return items.filter(filterHidden)
        .map((item) => ({...item, subItems: filter(item.subItems, filterHidden)}))
        .map(mapWithTitle)
        .map((item) => ({...item, subItems: item.subItems.map(mapWithTitle)}));
};
